import React, { useEffect, useState, useCallback } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { Modal, Button, Card, Alert } from 'react-bootstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api, { MapMarker, Company } from '../services/api';
import { track } from '../services/analytics';
import MadeWithBadge from './MadeWithBadge';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY_HERE';

// Validate and sanitize hex color to prevent injection
const sanitizeColor = (color: string | undefined): string | undefined => {
  if (!color) return undefined;
  const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  return hexColorRegex.test(color) ? color : undefined;
};

const ShareIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.499 2.499 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5z"/>
  </svg>
);

interface EmbeddedMapComponentProps {
  caseStudies: MapMarker[];
  onMarkerClick: (caseStudy: MapMarker) => void;
  markerColor?: string;
}

const EmbeddedMapComponent: React.FC<EmbeddedMapComponentProps> = ({ caseStudies, onMarkerClick, markerColor }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [map, setMap] = React.useState<google.maps.Map>();
  const clustererRef = React.useRef<MarkerClusterer | null>(null);

  React.useEffect(() => {
    if (ref.current && !map) {
      setMap(new window.google.maps.Map(ref.current, {
        center: { lat: 0, lng: 0 },
        zoom: 5,
        streetViewControl: false,
        mapId: 'a7ecd8e0022096171640a52b', // Required for Advanced Markers
      }));
    }
  }, [ref, map]);

  React.useEffect(() => {
    if (!map) return;
    let cancelled = false;

    const loadMarkers = async () => {
      const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
      if (cancelled) return;

      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current.setMap(null);
        clustererRef.current = null;
      }

      const bounds = new google.maps.LatLngBounds();
      const markers: google.maps.marker.AdvancedMarkerElement[] = [];

      caseStudies.forEach((caseStudy) => {
        const position = {
          lat: caseStudy.location.coordinates[1],
          lng: caseStudy.location.coordinates[0],
        };

        let pinElement;
        const safeColor = sanitizeColor(markerColor);
        if (safeColor) {
          pinElement = new PinElement({
            background: safeColor,
            borderColor: '#ffffff',
            glyphColor: '#ffffff',
            scale: 1.2,
          });
        }

        const marker = new AdvancedMarkerElement({
          position,
          title: caseStudy.projectName,
          content: pinElement?.element,
        });

        marker.addListener('click', () => {
          onMarkerClick(caseStudy);
        });

        markers.push(marker);
        bounds.extend(position);
      });

      clustererRef.current = new MarkerClusterer({
        map,
        markers,
        onClusterClick: (_event, cluster, clusterMap) => {
          if (!cluster.bounds) return;
          clusterMap.fitBounds(cluster.bounds);
          google.maps.event.addListenerOnce(clusterMap, 'idle', () => {
            const m = clusterMap as any;
            m.setZoom(m.getZoom() - 0.5);
          });
        },
      });

      if (caseStudies.length === 1) {
        // fitBounds on a single point would zoom to max; pull back to country-level instead.
        map.setCenter({
          lat: caseStudies[0].location.coordinates[1],
          lng: caseStudies[0].location.coordinates[0],
        });
        (map as any).setZoom(8);
      } else if (caseStudies.length > 0) {
        map.fitBounds(bounds);
        google.maps.event.addListenerOnce(map, 'idle', () => {
          const m = map as any;
          const z = m.getZoom();
          m.setZoom(z > 11 ? 11 : z - 0.5);
        });
      }
    };

    loadMarkers();

    return () => {
      cancelled = true;
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current.setMap(null);
        clustererRef.current = null;
      }
    };
  }, [map, caseStudies, onMarkerClick, markerColor]);

  return <div ref={ref} style={{ width: '100%', height: '100%' }} />;
};

interface EmbeddedMapViewProps {
  // Fallback used when neither URL param nor query string provides a clientId.
  // SolarMapPage passes 'demo-solar-uk' so the demo embed mounts without rewriting the URL.
  defaultClientId?: string;
}

const EmbeddedMapView: React.FC<EmbeddedMapViewProps> = ({ defaultClientId }) => {
  const [caseStudies, setCaseStudies] = useState<MapMarker[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<MapMarker | null>(null);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId?: string }>();
  const [searchParams] = useSearchParams();

  // Get clientId from URL params, query params, or the explicit prop fallback.
  const effectiveClientId = clientId || searchParams.get('clientId') || defaultClientId;

  const getShareUrl = (caseStudy: MapMarker) => {
    const base = window.location.origin;
    return effectiveClientId
      ? `${base}/${effectiveClientId}/case-study/${caseStudy._id}`
      : `${base}/case-study/${caseStudy._id}`;
  };

  const handleShare = async () => {
    if (selectedCaseStudy) {
      try {
        await navigator.clipboard.writeText(getShareUrl(selectedCaseStudy));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy URL:', err);
      }
    }
  };

  useEffect(() => {
    loadData();
    if (effectiveClientId) {
      track('embed_loaded', { clientId: effectiveClientId });
    }
  }, [effectiveClientId]);

  const loadData = async () => {
    try {
      const markerData = await api.getMapMarkers(effectiveClientId || undefined);
      const companyData = await api.getCompany(effectiveClientId || undefined);
      // Backend filters hideFromMap server-side; client filter kept as a cheap belt-and-suspenders
      // for the legacy fallback path.
      setCaseStudies(markerData.filter((cs) => !cs.hideFromMap));
      setCompany(companyData);
    } catch (err) {
      setError('Failed to load map data');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkerClick = useCallback((caseStudy: MapMarker) => {
    setActivePhotoIndex(0);
    setSelectedCaseStudy(caseStudy);
  }, []);

  const handleViewDetails = () => {
    if (!selectedCaseStudy) return;
    if (selectedCaseStudy.caseStudyUrl) {
      window.open(selectedCaseStudy.caseStudyUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    // Open in new tab/window to main site with user-scoped URL
    const url = effectiveClientId
      ? `${window.location.origin}/${effectiveClientId}/case-study/${selectedCaseStudy._id}`
      : `${window.location.origin}/case-study/${selectedCaseStudy._id}`;
    window.open(url, '_blank');
  };

  const render = (status: Status) => {
    if (status === Status.LOADING) return <div className="embed-loading">Loading Map...</div>;
    if (status === Status.FAILURE) return <div className="embed-error">Failed to load map</div>;
    return <EmbeddedMapComponent caseStudies={caseStudies} onMarkerClick={handleMarkerClick} markerColor={company?.markerColor} />;
  };

  if (loading) {
    return (
      <div className="embed-container">
        <div className="embed-loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="embed-container">
        <div className="embed-error">{error}</div>
      </div>
    );
  }

  if (caseStudies.length === 0) {
    return (
      <div className="embed-container">
        <div className="embed-loading" style={{ flexDirection: 'column', textAlign: 'center', padding: '1rem' }}>
          <h5 style={{ color: '#333', marginBottom: '0.5rem' }}>No records to display yet</h5>
          <div>This map doesn&rsquo;t have any records to show right now.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="embed-container">
      <style>{`
        .embed-container {
          position: relative;
          width: 100%;
          height: 100vh;
          margin: 0;
          padding: 0;
          overflow: hidden;
        }
        .embed-loading, .embed-error {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          font-family: Arial, sans-serif;
          color: #666;
        }
        .embed-error {
          color: #dc3545;
        }
        .embed-attribution {
          position: absolute;
          bottom: 10px;
          right: 10px;
          background: rgba(255, 255, 255, 0.9);
          padding: 5px 10px;
          border-radius: 4px;
          font-size: 12px;
          z-index: 1000;
        }
        .embed-attribution a {
          color: #007bff;
          text-decoration: none;
        }
        .embed-attribution a:hover {
          text-decoration: underline;
        }
        html, body {
          margin: 0;
          padding: 0;
          height: 100%;
          overflow: hidden;
        }
        #root {
          height: 100%;
        }
      `}</style>

      <Wrapper apiKey={GOOGLE_MAPS_API_KEY} render={render} />

      {/* Free-tier "Made with" badge — non-removable on free, drives upgrade pressure.
          Pro users get a plain "Powered by [their company]" attribution instead. */}
      {company?.ownerTier === 'free' ? (
        <MadeWithBadge variant="floating" />
      ) : (
        <div className="embed-attribution">
          Powered by <a href={window.location.origin} target="_blank" rel="noopener noreferrer">
            {company?.name || 'Map of Projects'}
          </a>
        </div>
      )}

      {/* Case Study Modal */}
      <Modal show={!!selectedCaseStudy} onHide={() => setSelectedCaseStudy(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedCaseStudy?.projectName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCaseStudy?.photos[0] && (
            <div className="mb-3">
              <div style={{ position: 'relative', maxHeight: '300px', width: '100%', overflow: 'hidden' }}>
                <img
                  key={activePhotoIndex}
                  src={selectedCaseStudy.photos[activePhotoIndex]?.url || selectedCaseStudy.photos[0].url}
                  alt={selectedCaseStudy.projectName}
                  className="img-fluid mb-2"
                  style={{
                    maxHeight: '300px',
                    width: '100%',
                    objectFit: 'cover',
                    animation: 'fadeIn 0.3s ease-in-out',
                  }}
                />
              </div>

              {/* Clickable thumbnails */}
              {selectedCaseStudy.photos.length > 1 && (
                <div className="d-flex flex-wrap gap-2 justify-content-center mt-2">
                  {selectedCaseStudy.photos.map((photo, index) => (
                    <img
                      key={index}
                      src={photo.url}
                      alt={`${selectedCaseStudy.projectName} - Photo ${index + 1}`}
                      onClick={() => setActivePhotoIndex(index)}
                      style={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'cover',
                        border: activePhotoIndex === index ? '2px solid #007bff' : '2px solid #dee2e6',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        opacity: activePhotoIndex === index ? 1 : 0.7,
                        transition: 'border-color 0.2s ease, opacity 0.2s ease',
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          {selectedCaseStudy?.approximateLocation && (
            <span className="badge bg-warning text-dark mb-2" style={{ fontSize: '0.75rem' }}>
              Approximate location
            </span>
          )}
          {selectedCaseStudy?.country && (
            <Card.Text>
              <strong>Country:</strong> {selectedCaseStudy.country}
            </Card.Text>
          )}
          <Card.Text>
            {selectedCaseStudy?.description.substring(0, 200)}
            {selectedCaseStudy && selectedCaseStudy.description.length > 200 && '...'}
          </Card.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant={copied ? "success" : "outline-secondary"}
            onClick={handleShare}
            title="Copy link to clipboard"
          >
            <ShareIcon /> {copied ? 'Copied!' : 'Share'}
          </Button>
          <Button variant="secondary" onClick={() => setSelectedCaseStudy(null)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleViewDetails}>
            {selectedCaseStudy?.caseStudyUrl ? 'Visit case study →' : 'View Details'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmbeddedMapView;